// @ts-check

/**
 * @file Provides a form-aware wrapper for async model-backed selects.
 */

import {useForm} from "@kaspernj/api-maker/build/form"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"

import Select from "./select"
import Text from "./text"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{text: string, value: string}} ModelSelectOption */
/** @typedef {{page?: number, searchValue?: string, values?: (string | number)[] | string | number}} ModelSelectOptionsLoaderArgs */
/** @typedef {{options: ModelSelectOption[], page?: number, pageSize?: number, totalCount?: number}} ModelSelectOptionsResult */
/** @typedef {(args?: ModelSelectOptionsLoaderArgs) => Promise<ModelSelectOptionsResult> | ModelSelectOptionsResult} ModelSelectOptionsLoader */
/** @typedef {(...args: unknown[]) => unknown} ModelSelectChangeHandler */

/**
 * @typedef {object} ModelSelectProps
 * @property {boolean} [closeOnChange] Close the dropdown after each selection change.
 * @property {string | number | null} [defaultValue] Initial single selected value.
 * @property {(string | number)[]} [defaultValues] Initial selected values.
 * @property {string} [label] Optional field label.
 * @property {boolean} [multiple] Allow multiple selections.
 * @property {string} [name] Api Maker form field name.
 * @property {ModelSelectChangeHandler} [onChangeValue] Selection change callback.
 * @property {ModelSelectOptionsLoader} options Async options loader.
 * @property {string} [placeholder] Empty select placeholder.
 * @property {boolean} [search] Whether search is enabled.
 * @property {string} testID Stable test id.
 * @property {(string | number)[]} [values] Controlled selected values.
 */

/**
 * @typedef {object} ModelSelectState
 * @property {string[]} selectedValues Current uncontrolled selected values.
 */

/**
 * Form-aware async select used by model-specific select components.
 * @augments {ShapeComponent<ModelSelectProps, ModelSelectState>}
 */
class ModelSelect extends ShapeComponent {
  /** @type {ReturnType<typeof useForm>} */
  form = null
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {boolean} */
  initialValuesPrimed = false
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = propTypesExact({
    closeOnChange: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValues: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    ),
    label: PropTypes.string,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChangeValue: PropTypes.func,
    options: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    search: PropTypes.bool,
    testID: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    )
  })
  static defaultProps = {
    closeOnChange: false,
    defaultValue: undefined,
    defaultValues: undefined,
    label: undefined,
    multiple: false,
    name: undefined,
    onChangeValue: undefined,
    placeholder: undefined,
    search: true,
    values: undefined
  }
  /** @type {ModelSelectState} */
  state = {
    selectedValues: []
  }

  /** Initializes theme and form value synchronization. */
  setup() {
    this.colors = useThemeColors()
    this.form = useForm()
    this.themeName = useThemeName()
    const defaultValue = this.p.defaultValue
    const defaultValuesKey = JSON.stringify(this.p.defaultValues || [])
    const multiple = this.p.multiple
    const name = this.p.name
    if (!this.initialValuesPrimed) {
      this.initialValuesPrimed = true
      this.state.selectedValues = this.selectedValuesFromDefaults()
    }

    useEffect(() => {
      const selectedValues = this.selectedValuesFromDefaults()
      this.s.selectedValues = selectedValues
      this.syncFormValue(selectedValues)
    }, [defaultValue, defaultValuesKey, multiple, name])
  }

  /**
   * Renders the model select.
   * @returns {ReactNode} Select field.
   */
  render() {
    const {
      closeOnChange,
      label,
      multiple,
      options,
      placeholder,
      search,
      testID,
      values
    } = this.p
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const selectedValues = Array.isArray(values)
      ? values.map((value) => String(value))
      : this.s.selectedValues
    const defaultValue = Array.isArray(values) || multiple
      ? undefined
      : selectedValues[0]
    const defaultValues = !Array.isArray(values) && multiple
      ? selectedValues
      : undefined

    return (
      <View testID={`${testID}/wrap`}>
        {label != null &&
          <Text
            style={styles[`label-${themeName}`] ||= {
              color: colors.textMuted,
              fontWeight: 600
            }}
            testID={`${testID}/label`}
          >
            {label}
          </Text>
        }
        <View
          style={styles[`selectWrap-${label != null}`] ||= {
            marginTop: label != null ? 6 : 0
          }}
          testID={`${testID}/selectWrap`}
        >
          <Select
            closeOnChange={closeOnChange}
            defaultValue={defaultValue}
            defaultValues={defaultValues}
            multiple={multiple}
            onChangeValue={this.tt.onChangeValue}
            options={options}
            placeholder={placeholder}
            search={Boolean(search)}
            testID={testID}
            values={Array.isArray(values) ? selectedValues : undefined}
          />
        </View>
      </View>
    )
  }

  /**
   * Applies a selection change.
   * @param {(string | number)[] | string | number | undefined} value Selected value.
   * @returns {void} Nothing.
   */
  onChangeValue = (value) => {
    const selectedValues = selectedValuesFromValue(value)
    this.s.selectedValues = selectedValues
    this.syncFormValue(selectedValues)
    this.p.onChangeValue?.(this.p.multiple ? selectedValues : selectedValues[0] || "")
  }

  /**
   * Reads initial selected values from props.
   * @returns {string[]} Initial selected values.
   */
  selectedValuesFromDefaults() {
    if (this.p.multiple && Array.isArray(this.p.defaultValues)) {
      return this.p.defaultValues.map((value) => String(value))
    }
    if (this.p.defaultValue != null && String(this.p.defaultValue).length > 0) {
      return [String(this.p.defaultValue)]
    }
    return []
  }

  /**
   * Syncs the selected values into the surrounding form.
   * @param {string[]} selectedValues Non-empty string ids stored by the select.
   * @returns {void} Nothing.
   */
  syncFormValue(selectedValues) {
    if (!this.tt.form || !this.p.name) return
    this.tt.form.setValue(
      this.p.name,
      this.p.multiple ? selectedValues : selectedValues[0] || ""
    )
  }
}

/**
 * Normalizes a HayaSelect change payload.
 * @param {(string | number)[] | string | number | undefined} value Raw callback payload from HayaSelect.
 * @returns {string[]} Non-empty string ids selected in the field.
 */
function selectedValuesFromValue(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).filter((entry) => entry.length > 0)
  }
  if (typeof value === "string" || typeof value === "number") {
    const stringValue = String(value)
    return stringValue.length > 0 ? [stringValue] : []
  }
  return []
}

/** @type {NamedExoticComponent<ModelSelectProps>} */
const ModelSelectComponent = memo(shapeComponent(ModelSelect))

export default ModelSelectComponent
